import { useEffect } from "react";
import { postMqttStats } from "@client/api/endpoints";
import { isStatsFresh, useStatsStore } from "@client/store/stats";
import { useUserStore } from "@client/store/user";

/**
 * TTL-cached fetch pro `/api/mqtt/stats` (denní historie). Refetchuje jen pokud
 * cached hodnota expirovala (5 min) — sbalení/rozbalení HidingWrapperu neudělá nový request.
 *
 * Viz docs/plans/CHART-DATA-REFACTOR.md (Fáze 5).
 */
export const useStatsHistory = (topicId: string): { stats: dailyStats[]; loading: boolean } => {
  const user = useUserStore((s) => s.user);
  const cached = useStatsStore((s) => s.cache.get(`history|${topicId}`));

  useEffect(() => {
    if (!user || !topicId) return;
    const existing = useStatsStore.getState().get(`history|${topicId}`);
    if (existing?.kind === "history" && isStatsFresh(existing, Date.now())) return;

    let aborted = false;
    postMqttStats({ topicId })
      .then((res) => {
        if (aborted || !res.success) return;
        useStatsStore.getState().put(`history|${topicId}`, {
          kind: "history",
          data: res.responseObject,
          fetchedAt: Date.now(),
        });
      })
      .catch(() => {
        // network error — necachovat
      });

    return () => {
      aborted = true;
    };
  }, [user, topicId]);

  const stats = cached?.kind === "history" ? cached.data : [];
  return { stats, loading: !cached };
};
